import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";

interface WorldInfo {
  name: string;
  icon: string;
  color: string;
}

const WORLDS: Record<string, WorldInfo> = {
  greatHall: { name: "The Great Hall", icon: "\u26A1", color: "#ffd700" },
  maraudersMap: { name: "Marauder's Map", icon: "\uD83D\uDDFA", color: "#c8a050" },
  library: { name: "The Restricted Section", icon: "\uD83D\uDCD6", color: "#9b7fd4" },
  greatLake: { name: "Hogwarts Grounds", icon: "\uD83C\uDFF0", color: "#7fb3d4" },
  duellingClub: { name: "The Duelling Club", icon: "\u2694", color: "#ffa116" },
  forbiddenForest: { name: "The Forbidden Forest", icon: "\uD83C\uDF32", color: "#5fa463" },
  owlery: { name: "The Owlery", icon: "\uD83E\uDD89", color: "#c8a050" },
};

const WorldIndicator: React.FC = () => {
  const location = useLocation();
  const [world, setWorld] = useState<WorldInfo>(WORLDS.greatHall);
  const [scrollSection, setScrollSection] = useState("");

  // Detect which section is in view
  useEffect(() => {
    const checkSection = () => {
      const heroEl = document.getElementById("hero");
      const aboutEl = document.getElementById("about");
      const heroTimeEl = document.getElementById("map-marker");
      const scrollY = window.scrollY;
      const vh = window.innerHeight;

      if (heroTimeEl) {
        const rect = heroTimeEl.getBoundingClientRect();
        if (rect.top < vh * 0.5 && rect.bottom > -vh) {
          setScrollSection("maraudersMap");
          return;
        }
      }

      if (aboutEl) {
        const rect = aboutEl.getBoundingClientRect();
        if (rect.top < vh * 0.5 && rect.bottom > vh * 0.2) {
          setScrollSection("greatHall");
          return;
        }
      }

      if (heroEl) {
        const rect = heroEl.getBoundingClientRect();
        if (rect.top < vh * 0.3 && rect.bottom > 0) {
          setScrollSection("greatHall");
          return;
        }
      }

      setScrollSection("");
    };

    window.addEventListener("scroll", checkSection, { passive: true });
    checkSection();
    return () => window.removeEventListener("scroll", checkSection);
  }, []);

  // Map route + scroll position to world
  useEffect(() => {
    const path = location.pathname;

    // Each route is a place in the castle
    const BY_ROUTE: Record<string, WorldInfo> = {
      "/skills": WORLDS.greatLake,
      "/experience": WORLDS.greatLake,
      "/education": WORLDS.library,
      "/projects": WORLDS.library,
      "/leetcode": WORLDS.duellingClub,
      "/opensource": WORLDS.forbiddenForest,
      "/contact": WORLDS.owlery,
    };

    if (path === "/") {
      // Home page — depends on scroll
      setWorld(scrollSection === "maraudersMap" ? WORLDS.maraudersMap : WORLDS.greatHall);
    } else {
      setWorld(BY_ROUTE[path] || WORLDS.greatHall);
    }
  }, [location.pathname, scrollSection]);

  return (
    <Indicator $color={world.color} key={world.name}>
      <WorldIcon>{world.icon}</WorldIcon>
      <WorldName $color={world.color}>{world.name}</WorldName>
    </Indicator>
  );
};

export default WorldIndicator;

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Indicator = styled.div<{ $color: string }>`
  position: fixed;
  top: 70px;
  left: 1.5rem;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  background: rgba(10, 8, 14, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid ${({ $color }) => $color}20;
  border-radius: 100px;
  animation: ${fadeSlide} 0.4s ease both;
  cursor: none;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 65px;
    left: 0.8rem;
    padding: 0.2rem 0.5rem;
  }
`;

const WorldIcon = styled.span`
  font-size: 0.7rem;
  line-height: 1;
`;

const WorldName = styled.span<{ $color: string }>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  color: ${({ $color }) => $color};
  letter-spacing: 0.1em;
  white-space: nowrap;
  text-transform: uppercase;
`;
